import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ChatCircleDots, X, Plus, CaretDown, CaretRight, PaperPlaneRight, ArrowLeft, User, Hash, Trash,
} from "@phosphor-icons/react";

/**
 * Floating internal-chat button + slide-over panel.
 * Shown for company_owner and attendants only.
 */
export default function InternalChatPanel() {
  const { user } = useAuth();
  const enabled = !!user && (user.role === "company_owner" || user.role === "attendant");

  const [open, setOpen] = useState(false);
  const [convs, setConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [expandChannels, setExpandChannels] = useState(true);
  const [expandDirects, setExpandDirects] = useState(true);
  const [openNewChannel, setOpenNewChannel] = useState(false);
  const [openNewDirect, setOpenNewDirect] = useState(false);
  const [teammates, setTeammates] = useState([]);
  const [newChannel, setNewChannel] = useState({ name: "", member_ids: [] });
  const [newDirectUser, setNewDirectUser] = useState("");
  const scrollerRef = useRef(null);

  // Total unread (used for badge on floating button)
  const totalUnread = useMemo(
    () => convs.reduce((acc, c) => acc + (c.unread_count || 0), 0),
    [convs],
  );

  // Poll convs every 5s (always, so badge stays fresh)
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/internal-chat/conversations");
        if (!cancelled) setConvs(data);
      } catch { /* ignore */ }
    };
    load();
    const it = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(it); };
  }, [enabled]);

  // Poll messages of active conv every 3s
  useEffect(() => {
    if (!activeConv) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/internal-chat/conversations/${activeConv.id}/messages`);
        if (!cancelled) setMessages(data);
        api.post(`/internal-chat/conversations/${activeConv.id}/mark-read`).catch(() => {});
      } catch { /* ignore */ }
    };
    load();
    const it = setInterval(load, 3000);
    return () => { cancelled = true; clearInterval(it); };
  }, [activeConv?.id]);

  // Auto-scroll on new msgs
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages.length, activeConv?.id]);

  // Load teammates when opening the "new" dialogs
  const loadTeammates = async () => {
    try {
      const { data } = await api.get("/company/attendants");
      setTeammates((data || []).filter((u) => u.id !== user?.id));
    } catch { /* ignore */ }
  };

  const openChannelDialog = () => {
    setNewChannel({ name: "", member_ids: [] });
    loadTeammates();
    setOpenNewChannel(true);
  };
  const openDirectDialog = () => {
    setNewDirectUser("");
    loadTeammates();
    setOpenNewDirect(true);
  };

  const saveChannel = async () => {
    if (!newChannel.name.trim()) return toast.error("Nome obrigatório");
    try {
      const { data } = await api.post("/internal-chat/channels", newChannel);
      toast.success("Canal criado");
      setOpenNewChannel(false);
      setActiveConv(data);
      // refresh list
      const list = await api.get("/internal-chat/conversations");
      setConvs(list.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveDirect = async () => {
    if (!newDirectUser) return toast.error("Escolha um colaborador");
    try {
      const { data } = await api.post("/internal-chat/directs", { other_user_id: newDirectUser });
      setOpenNewDirect(false);
      setActiveConv(data);
      const list = await api.get("/internal-chat/conversations");
      setConvs(list.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendMessage = async () => {
    if (!text.trim() || !activeConv) return;
    try {
      await api.post(`/internal-chat/conversations/${activeConv.id}/messages`, { body: text.trim() });
      setText("");
      // reload list to update last_message_at ordering
      const list = await api.get("/internal-chat/conversations");
      setConvs(list.data);
      const m = await api.get(`/internal-chat/conversations/${activeConv.id}/messages`);
      setMessages(m.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const leaveConv = async () => {
    if (!activeConv) return;
    const label = activeConv.type === "direct" ? "Excluir esta conversa direta?" : `Sair do canal "${activeConv.name}"?`;
    if (!window.confirm(label)) return;
    try {
      await api.delete(`/internal-chat/conversations/${activeConv.id}`);
      setActiveConv(null);
      const list = await api.get("/internal-chat/conversations");
      setConvs(list.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!enabled) return null;

  const channels = convs.filter((c) => c.type === "channel");
  const directs = convs.filter((c) => c.type === "direct");

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="internal-chat-trigger"
          className="fixed bottom-24 right-6 z-40 h-14 w-14 rounded-full bg-[#0F4C3A] hover:bg-[#0A3629] text-white flex items-center justify-center shadow-brutal transition-transform hover:scale-105"
          title="Chat interno"
        >
          <ChatCircleDots size={26} weight="bold" />
          {totalUnread > 0 && (
            <span
              data-testid="internal-chat-badge"
              className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
            >
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      )}

      {/* Slide-over panel */}
      {open && (
        <div className="fixed inset-y-0 right-0 z-40 w-[380px] max-w-[92vw] bg-white border-l border-[#E5E5E0] shadow-2xl flex flex-col" data-testid="internal-chat-panel">
          <div className="h-14 bg-[#0F4C3A] text-white px-4 flex items-center justify-between">
            <div className="flex items-center gap-2 font-bold">
              {activeConv ? (
                <button onClick={() => setActiveConv(null)} data-testid="ic-back-btn" className="hover:opacity-80">
                  <ArrowLeft size={18} weight="bold" />
                </button>
              ) : (
                <ChatCircleDots size={18} weight="bold" />
              )}
              <span className="truncate">
                {activeConv ? activeConv.name : "Chat Interno"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {activeConv && (
                <button onClick={leaveConv} data-testid="ic-leave-btn" className="hover:opacity-80" title="Sair/Excluir">
                  <Trash size={18} weight="bold" />
                </button>
              )}
              <button onClick={() => { setOpen(false); setActiveConv(null); }} data-testid="internal-chat-close" className="hover:opacity-80">
                <X size={20} weight="bold" />
              </button>
            </div>
          </div>

          {!activeConv ? (
            /* Conversation list */
            <div className="flex-1 overflow-y-auto p-3 bg-[#F7F7F5]" data-testid="ic-conv-list">
              {/* Canais */}
              <button
                onClick={() => setExpandChannels((v) => !v)}
                data-testid="ic-toggle-channels"
                className="w-full flex items-center gap-2 py-2 text-sm font-bold text-[#1A1A1A]"
              >
                {expandChannels ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                Canais
              </button>
              {expandChannels && (
                <div className="mb-3">
                  <button
                    onClick={openChannelDialog}
                    data-testid="ic-new-channel-btn"
                    className="w-full flex items-center gap-3 px-2 py-2 text-sm text-[#0F4C3A] hover:bg-[#EDF3F0]"
                  >
                    <div className="h-6 w-6 rounded-full border-2 border-[#0F4C3A] flex items-center justify-center">
                      <Plus size={14} weight="bold" />
                    </div>
                    novo canal
                  </button>
                  {channels.map((c) => (
                    <ConvRow key={c.id} conv={c} onClick={() => setActiveConv(c)} icon={Hash} />
                  ))}
                </div>
              )}

              {/* Conversas Diretas */}
              <button
                onClick={() => setExpandDirects((v) => !v)}
                data-testid="ic-toggle-directs"
                className="w-full flex items-center gap-2 py-2 text-sm font-bold text-[#1A1A1A]"
              >
                {expandDirects ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                Conversas Diretas
              </button>
              {expandDirects && (
                <div>
                  <button
                    onClick={openDirectDialog}
                    data-testid="ic-new-direct-btn"
                    className="w-full flex items-center gap-3 px-2 py-2 text-sm text-[#0F4C3A] hover:bg-[#EDF3F0]"
                  >
                    <div className="h-6 w-6 rounded-full border-2 border-[#0F4C3A] flex items-center justify-center">
                      <Plus size={14} weight="bold" />
                    </div>
                    nova conversa direta
                  </button>
                  {directs.map((c) => (
                    <ConvRow key={c.id} conv={c} onClick={() => setActiveConv(c)} icon={User} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Active conversation */
            <>
              <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 bg-[#EDF3F0] space-y-2" data-testid="ic-messages">
                {messages.length === 0 && (
                  <div className="text-center text-xs text-[#5C5C59] mt-6">Sem mensagens ainda. Diga oi.</div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] px-3 py-2 text-sm shadow-sm ${mine ? "bg-[#0F4C3A] text-white" : "bg-white text-[#1A1A1A] border border-[#E5E5E0]"}`}>
                        {!mine && <div className="text-[10px] font-bold text-[#0F4C3A] mb-0.5">{m.sender_name}</div>}
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={`text-[9px] mt-1 ${mine ? "text-white/60" : "text-[#5C5C59]"}`}>
                          {new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-3 border-t border-[#E5E5E0] bg-white flex items-center gap-2">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMessage())}
                  placeholder="Digite uma mensagem…"
                  data-testid="ic-message-input"
                  className="rounded-none"
                />
                <Button onClick={sendMessage} data-testid="ic-send-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                  <PaperPlaneRight size={16} weight="bold" />
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* New Channel Dialog */}
      <Dialog open={openNewChannel} onOpenChange={setOpenNewChannel}>
        <DialogContent className="bg-white rounded-none">
          <DialogHeader><DialogTitle>Novo canal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Nome</Label>
              <Input
                value={newChannel.name}
                onChange={(e) => setNewChannel({ ...newChannel, name: e.target.value })}
                data-testid="ic-channel-name-input"
                className="rounded-none mt-1"
                placeholder="ex: Vendas"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Membros</Label>
              <div className="mt-2 border border-[#E5E5E0] max-h-56 overflow-y-auto">
                {teammates.length === 0 && (
                  <div className="p-3 text-center text-xs text-[#5C5C59]">Nenhum colaborador disponível.</div>
                )}
                {teammates.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 cursor-pointer hover:bg-[#F7F7F5]">
                    <input
                      type="checkbox"
                      checked={newChannel.member_ids.includes(t.id)}
                      onChange={() => setNewChannel((f) => ({
                        ...f,
                        member_ids: f.member_ids.includes(t.id)
                          ? f.member_ids.filter((x) => x !== t.id)
                          : [...f.member_ids, t.id],
                      }))}
                      data-testid={`ic-channel-member-${t.id}`}
                      className="accent-[#0F4C3A]"
                    />
                    <span className="text-sm">{t.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenNewChannel(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={saveChannel} data-testid="ic-channel-save-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Direct Dialog */}
      <Dialog open={openNewDirect} onOpenChange={setOpenNewDirect}>
        <DialogContent className="bg-white rounded-none">
          <DialogHeader><DialogTitle>Nova conversa direta</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Colaborador</Label>
            <div className="mt-2 border border-[#E5E5E0] max-h-64 overflow-y-auto">
              {teammates.length === 0 && (
                <div className="p-3 text-center text-xs text-[#5C5C59]">Nenhum colaborador disponível.</div>
              )}
              {teammates.map((t) => (
                <label key={t.id} className={`flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 cursor-pointer hover:bg-[#F7F7F5] ${newDirectUser === t.id ? "bg-[#EDF3F0]" : ""}`}>
                  <input
                    type="radio"
                    name="direct-user"
                    checked={newDirectUser === t.id}
                    onChange={() => setNewDirectUser(t.id)}
                    data-testid={`ic-direct-user-${t.id}`}
                    className="accent-[#0F4C3A]"
                  />
                  <span className="text-sm">{t.name}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenNewDirect(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={saveDirect} data-testid="ic-direct-save-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Abrir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ConvRow({ conv, onClick, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={`ic-conv-${conv.id}`}
      className="w-full flex items-center gap-3 px-2 py-2 text-sm hover:bg-white text-left group"
    >
      <div className="h-8 w-8 rounded-full bg-[#EDF3F0] flex items-center justify-center flex-shrink-0">
        <Icon size={14} weight="bold" className="text-[#0F4C3A]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold truncate text-[#1A1A1A]">{conv.name || "Conversa"}</span>
          {conv.unread_count > 0 && (
            <span className="text-[9px] bg-[#DC2626] text-white px-1.5 py-0.5 rounded-full font-bold">
              {conv.unread_count > 99 ? "99+" : conv.unread_count}
            </span>
          )}
        </div>
        {conv.last_message && (
          <div className="text-[11px] text-[#5C5C59] truncate">{conv.last_message}</div>
        )}
      </div>
    </button>
  );
}
